/**
 * Crossfire high-value cart assistance.
 * Appears once per browser tab session after 12s of cart inactivity.
 * Only the Shopify Liquid snippet for carts >= CAD 1,000 can trigger it.
 */
(() => {
  const SESSION_KEY = 'cf-cart-assist-seen-v1';
  const IDLE_MS = 12000;
  let active = null;
  let timer = null;
  let seenInMemory = false;
  let lastPointerMove = 0;

  const wasSeen = () => {
    try { return sessionStorage.getItem(SESSION_KEY) === '1' || seenInMemory; }
    catch (err) { return seenInMemory; }
  };

  const markSeen = () => {
    seenInMemory = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); }
    catch (err) { /* Storage can be disabled; keep session-local fallback. */ }
  };

  function clearWait() {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  }

  function visibleCartPanel() {
    const panels = document.querySelectorAll('[data-cf-cart-assist]');
    for (const panel of panels) {
      const drawer = panel.closest('cart-drawer');
      if (drawer && (drawer.getAttribute('aria-hidden') === 'true' || !drawer.hasAttribute('open'))) continue;
      return panel;
    }
    return null;
  }

  function updateStickyCartHeight() {
    document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
  }

  function schedule() {
    clearWait();
    if (wasSeen()) return;
    active = visibleCartPanel();
    if (!active || !active.hidden) return;

    const candidate = active;
    timer = window.setTimeout(() => {
      timer = null;
      if (wasSeen() || !candidate.isConnected || visibleCartPanel() !== candidate) return;
      candidate.hidden = false;
      markSeen();
      updateStickyCartHeight();
    }, IDLE_MS);
  }

  function onInteraction(event) {
    if (wasSeen() || !active || !active.hidden) return;
    const scope = active.closest('cart-drawer') || active.closest('#cart');
    if (!scope || !scope.contains(event.target)) return;
    if (event.type === 'pointermove') {
      const now = Date.now();
      if (now - lastPointerMove < 1200) return;
      lastPointerMove = now;
    }
    schedule();
  }

  document.addEventListener('click', event => {
    const dismiss = event.target.closest('[data-cf-cart-assist-dismiss]');
    if (!dismiss) return;
    const panel = dismiss.closest('[data-cf-cart-assist]');
    if (panel) panel.hidden = true;
    markSeen();
    clearWait();
    updateStickyCartHeight();
  });

  ['pointermove', 'pointerdown', 'keydown', 'touchstart', 'wheel'].forEach(type => {
    document.addEventListener(type, onInteraction, { passive: true });
  });

  document.addEventListener('on:cart-drawer:after-open', schedule);
  document.addEventListener('on:cart-drawer:before-close', () => {
    clearWait();
    active = null;
  });
  document.addEventListener('on:cart:add', () => window.setTimeout(schedule, 100));
  document.addEventListener('on:line-item:change', () => window.setTimeout(schedule, 100));
  document.addEventListener('dispatch:cart-drawer:refresh', () => window.setTimeout(schedule, 500));
  document.addEventListener('shopify:section:load', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
